import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { DropdownMenu } from "@radix-ui/react-dropdown-menu";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Badge } from "@components/badge/badge";
import { Button } from "@components/button/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/card/card";
import { DropdownMenuTrigger } from "@components/dropdown-menu/dropdown-menu";
import { ImagePreviewModal } from "@components/image-preview-modal";
import { SearchInput } from "@components/search/search";
import { SpinnerTable } from "@components/spinner-table/spinner-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/table/table";

import { cn } from "@/data/lib/tailwind-merge";
import { getFuroColorClass } from "@/utils/get-furo-color-class";
import { getPrazoProducaoColorClass } from "@/utils/get-prazo-producao-color-class";
import { getSituacaoColorClass } from "@/utils/get-situacao-color-class";
import { handleLimitTextdisplayByAmount } from "@/utils/text-limit";

export function DynamicTable({
  title,
  description,
  subDescripion,
  isLoadingPage,
  data,
  totalItems,
  itemsPerPage,
  columns,
  checkBox = false,
  actions = [],
  selectedItems = [],
  onSelectAll,
  onSelectItem,
  childerSearch,
  link = "",
  paginations = [],
  onGoToPage = () => {}, //
  currentPage,
  lastPage,
  fillViewport = false,
  rowClassName,
  onRowClick,
}: any) {
  const router = useNavigate();
  const [imageToPreview, setImageToPreview] = useState<string | null>(null);

  const isAllSelected = selectedItems.length === data.length;

  // cliques em checkbox, botões de ação e links dentro da linha não disparam o clique da linha
  const ehCliqueEmElementoInterativo = (event: React.MouseEvent) =>
    (event.target as HTMLElement).closest("a, button, input, label, select, textarea") !== null;

  const executarAcaoDoCliqueNaLinha = (event: React.MouseEvent, item: any) => {
    if (ehCliqueEmElementoInterativo(event)) return;
    if (onRowClick) return onRowClick(item);
    if (link !== "") router(link);
  };

  const renderCellText = (text: string, limit?: number) =>
    fillViewport ? text : handleLimitTextdisplayByAmount({ text, limit: limit || 20 });

  const actionsColumnWidth =
    fillViewport && actions.length > 0
      ? `${actions.length * 2 + (actions.length - 1) * 0.25 + 2}rem`
      : undefined;

  const actionsColumnStyle = actionsColumnWidth
    ? { width: actionsColumnWidth, minWidth: actionsColumnWidth }
    : undefined;

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Card className={cn(fillViewport && "flex h-full min-h-0 w-full min-w-0 flex-col")}>
      <CardHeader className={cn("items-center justify-between", fillViewport && "shrink-0")}>
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          {subDescripion && (
            <CardDescription className="mt-4 text-sm font-bold text-gray-700">
              {subDescripion}
            </CardDescription>
          )}
        </div>
        <div>{childerSearch ? childerSearch : <SearchInput />}</div>
      </CardHeader>

      {isLoadingPage ? (
        <SpinnerTable />
      ) : (
        <CardContent
          className={cn(
            fillViewport
              ? "min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-2 md:px-3"
              : "overflow-auto"
          )}
        >
          <Table
            containerClassName={fillViewport ? "w-full min-w-0 overflow-visible" : undefined}
            className={cn(fillViewport ? "w-full min-w-0 table-fixed" : "min-w-full")}
          >
            {fillViewport && (
              <colgroup>
                {checkBox && <col className="w-14" />}
                {columns?.map((_: any, index: number) => (
                  <col key={index} />
                ))}
                {actions.length > 0 && <col style={actionsColumnStyle} />}
              </colgroup>
            )}
            <TableHeader>
              <TableRow>
                {checkBox && (
                  <TableHead className={cn("w-4", fillViewport && "w-14 py-3 align-middle")}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={onSelectAll}
                      className="form-checkbox h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </TableHead>
                )}

                {columns?.map((column: any, index: number) => (
                  <TableHead
                    key={index}
                    className={cn(
                      "text-left",
                      fillViewport
                        ? "h-auto min-h-12 min-w-0 whitespace-normal break-words py-3 align-middle"
                        : "whitespace-nowrap",
                      !fillViewport && index >= 2 && "hidden md:table-cell"
                    )}
                  >
                    {column.label}
                  </TableHead>
                ))}

                {actions.length > 0 && (
                  <TableHead
                    style={actionsColumnStyle}
                    className={cn(
                      "whitespace-nowrap text-left",
                      fillViewport ? "h-auto min-h-12 py-3 align-middle" : "w-16"
                    )}
                  >
                    Ações
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (checkBox ? 1 : 0) + (actions.length > 0 ? 1 : 0)}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <img
                        src="https://images.icon-icons.com/3299/PNG/512/documents_denied_cross_error_icon_208853.png" // ou o caminho correto da imagem
                        alt="Nenhum dado"
                        className="h-30 w-40 opacity-70"
                      />
                      <p className="text-base font-medium text-gray-500">Nenhum dado encontrado.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((item: any, index: number) => (
                  <TableRow
                    key={index}
                    className={cn("cursor-pointer", rowClassName?.(item))}
                    onClick={(event) => executarAcaoDoCliqueNaLinha(event, item)}
                  >
                    {checkBox && (
                      <TableCell className={cn("w-4", fillViewport && "w-14 align-top")}>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => onSelectItem(item.id)}
                          className="form-checkbox h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </TableCell>
                    )}

                    {columns.map((column: any, colIndex: number) => {
                      const value = item[column.accessor];

                      return (
                        <TableCell
                          key={colIndex}
                          className={cn(
                            "text-left",
                            fillViewport
                              ? "min-w-0 whitespace-normal break-words align-top"
                              : "max-w-xs whitespace-normal break-words",
                            !fillViewport && colIndex >= 2 && "hidden md:table-cell"
                          )}
                        >
                          {column.isBadge ? (
                            <Badge
                              className={
                                column.label === "Prazo"
                                  ? getPrazoProducaoColorClass(value)
                                  : column.label === "Furo"
                                    ? getFuroColorClass(value)
                                    : getSituacaoColorClass(value)
                              }
                            >
                              {column.customRender
                                ? column.customRender(value, item)
                                : renderCellText(value, column.limit)}
                            </Badge>
                          ) : column.isImage ? (
                            value ? (
                              <img
                                src={value}
                                alt={column.label}
                                className="h-auto w-20 cursor-pointer rounded object-contain shadow-sm transition hover:scale-105"
                                onClick={() => setImageToPreview(value)}
                              />
                            ) : (
                              <span className="text-sm text-gray-400">Sem imagem</span>
                            )
                          ) : column.customRender ? (
                            column.customRender(value, item)
                          ) : (
                            renderCellText(value, column.limit)
                          )}
                        </TableCell>
                      );
                    })}

                    {actions.length > 0 && (
                      <TableCell
                        style={actionsColumnStyle}
                        className={cn(
                          "whitespace-nowrap",
                          fillViewport ? "pr-2 align-top" : "w-16"
                        )}
                      >
                        <div
                          className={cn(
                            "flex",
                            fillViewport ? "justify-start gap-1" : "justify-center space-x-2"
                          )}
                        >
                          {actions.map((action: any, actionIndex: number) => {
                            const isVisible =
                              typeof action.visible === "function"
                                ? action.visible(item)
                                : action.visible !== false;

                            if (!isVisible) return null;

                            return (
                              <DropdownMenu key={actionIndex}>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    aria-haspopup="true"
                                    size="icon"
                                    variant="ghost"
                                    className={cn(fillViewport && "h-8 w-8 shrink-0")}
                                    disabled={
                                      typeof action.disabled === "function"
                                        ? action.disabled(item)
                                        : action.disabled
                                    }
                                    onClick={() => action.onClick(item)}
                                    title={action.label}
                                  >
                                    <action.icon
                                      className={
                                        typeof action.iconClassName === "function"
                                          ? action.iconClassName(item)
                                          : (action.iconClassName ?? "h-4 w-4")
                                      }
                                    />
                                    <span className="sr-only">{action.label}</span>
                                  </Button>
                                </DropdownMenuTrigger>
                              </DropdownMenu>
                            );
                          })}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {imageToPreview && (
            <ImagePreviewModal
              open={!!imageToPreview}
              onClose={() => setImageToPreview(null)}
              imageUrl={imageToPreview}
            />
          )}
        </CardContent>
      )}

      <CardFooter className={cn(fillViewport && "shrink-0")}>
        <div className="flex w-full flex-col items-center justify-between space-y-2 md:flex-row md:space-y-0">
          <div className="text-xs text-muted-foreground">
            {`Mostrando ${start} - ${end} de ${totalItems} registros`}
          </div>

          {/* Paginação dinâmica completa */}
          <div className="flex space-x-1">
            <button
              onClick={() => onGoToPage("prev")}
              disabled={currentPage === 1}
              className="rounded border bg-white px-3 py-1 text-black"
              title="Página anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {paginations.map((page: any, idx: number) => (
              <button
                key={idx}
                onClick={() => typeof page === "number" && onGoToPage(page)}
                disabled={page === "..."}
                className={`rounded border px-3 py-1 ${
                  page === currentPage
                    ? "hover:bg-primary/9 bg-primary text-primary-foreground"
                    : "bg-white text-black"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => onGoToPage("next")}
              disabled={currentPage === lastPage}
              className="rounded border bg-white px-3 py-1 text-black"
              title="Próxima página"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
